import { createContext, ReactNode, useContext, useMemo } from 'react';
import { FieldProvenanceType } from '../../schemas/report.schemas';
import {
    buildProvenanceLookup,
    EMPTY_PROVENANCE_LOOKUP,
    ProvenanceLookupType,
} from '../../utils/provenanceUtils';

type SourceViewContextValueType = {
    sourceViewEnabled: boolean;
    onValueClick: (provenance: FieldProvenanceType[] | null) => void;
    getProvenance: ProvenanceLookupType;
};

const SourceViewContext = createContext<SourceViewContextValueType | null>(null);

type SourceViewProviderPropsType = Omit<SourceViewContextValueType, 'getProvenance'> & {
    reports: { id: number | string; provenance?: FieldProvenanceType[] }[];
    children: ReactNode;
};

export function SourceViewProvider({
    sourceViewEnabled,
    onValueClick,
    reports,
    children,
}: SourceViewProviderPropsType) {
    const getProvenance = useMemo(
        () => (sourceViewEnabled ? buildProvenanceLookup(reports) : EMPTY_PROVENANCE_LOOKUP),
        [sourceViewEnabled, reports],
    );
    const value = useMemo(
        () => ({ sourceViewEnabled, onValueClick, getProvenance }),
        [sourceViewEnabled, onValueClick, getProvenance],
    );

    return <SourceViewContext.Provider value={value}>{children}</SourceViewContext.Provider>;
}

export function useSourceView(): SourceViewContextValueType {
    const context = useContext(SourceViewContext);
    if (context === null) {
        throw new Error('useSourceView must be used within a SourceViewProvider');
    }
    return context;
}
